/**
 * BatchPayoutPage — WP-D (Sammel-Auszahlungsseite, /settlements/payout)
 *
 * Loest den interimistischen Client-Only-Ablauf aus PayoutBatchSheet.tsx
 * (WP12, ohne eigenen BE-Endpunkt, PATCH je Settlement) ab: der BE-Vertrag
 * fuer den echten Sammel-Endpunkt ist jetzt fertig (POST
 * /api/v2/commissions/pay), siehe useBatchPayout.ts. Die Seite gruppiert
 * faellige (approved, HNT) Abrechnungen serverseitig zu Bloecken mit je
 * einem Multi-Empfaenger-QR (max. 19/29 Empfaenger je Block, Kontrakt-
 * Server-Limit) statt einer 1-Host-pro-Karte-Sequenz.
 *
 * Wiederaufnahme: `?payoutId=` in der URL ist die einzige clientseitig
 * gehaltene Zustandsquelle ueber einen Reload hinweg — beim (Wieder-)Laden
 * holt useBatchPayoutDetail() den kompletten Stand per GET
 * /api/v2/commissions/:id frisch vom Server (Bezahlt-Status je Block ist
 * dort maßgeblich, nicht ein lokaler Zwischenstand).
 *
 * Block-Index (welcher Block gerade angezeigt wird) wird NUR beim Wechsel
 * auf eine andere payout_id neu initialisiert (Render-Zeit-Reset, gleiches
 * lint-konforme Muster wie PayoutSheet.tsx/PayoutBatchSheet.tsx) — ein
 * "Als bezahlt markieren" merged nur das eine betroffene Block-Objekt in den
 * Cache (siehe useMarkBlockPaid) und darf die Navigation NICHT zuruecksetzen.
 *
 * Einzel-QR-Fallback (Umschalter, Punkt 3 im Auftrag): kein neuer Endpunkt
 * vorgesehen — nutzt denselben `payout.solana_pay_uri`, den GET
 * /api/v2/settlements ohnehin je Zeile liefert (useDueSettlements, bereits
 * fuer WP12 genutzt). Das ist nur exakt korrekt, wenn ein Payee GENAU EINER
 * Abrechnung entspricht (payee.settlement_ids.length === 1) — hat ein Host
 * mehrere approved HNT-Abrechnungen im Zeitraum, aggregiert der Batch-
 * Endpunkt sie zu EINEM Payee-Betrag, fuer den es keine einzelne
 * Abrechnungszeile mit passendem Betrag gibt. Fuer diesen Fall wird bewusst
 * KEIN Einzel-QR erzeugt (der Betrag waere falsch) — stattdessen ein Hinweis,
 * den Sammel-QR zu nutzen. Bekannte Grenze: useDueSettlements deckt nur noch
 * NICHT bezahlte (weiterhin 'approved') Abrechnungen ab; fuer einen bereits
 * als bezahlt markierten Block liefert der Fallback deshalb keinen QR mehr
 * (unkritisch, da dort ohnehin keine Aktion mehr noetig ist).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { QRCodeSVG } from 'qrcode.react'
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react'
import { formatNumber } from '../../components/ui/format'
import { ApiError } from '../../lib/errors/ApiError'
import { PremiumRequired } from '../../components/PremiumRequired'
import { useAccount } from '../settings/useAccount'
import { isPremiumRequiredError } from '../../lib/premiumError'
import {
  useStartBatchPayout,
  useBatchPayoutDetail,
  useMarkBlockPaid,
  type BatchPayoutBlock,
  type BatchPayoutPayee,
  type BatchPayoutResponse,
  type BatchPayoutSkipped,
} from './useBatchPayout'
import { useDueSettlements } from './useSettlements'
import './settlements.css'
import './batchPayout.css'

const QR_SIZE_MULTI = 240
const QR_SIZE_SINGLE = 140

function fmtHnt(value: number | undefined | null): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return '—'
  return `${formatNumber(value, 8)} HNT`
}

function isInvalidPeriodMonth(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 422 &&
    (error.body as { error?: string } | undefined)?.error === 'invalid_period_month'
  )
}

// --- Start-Panel (kein payoutId in der URL) ----------------------------------

function StartPanel({ onStarted }: { onStarted: (payoutId: number) => void }) {
  const { t } = useTranslation('settlements')
  const start = useStartBatchPayout()
  const [periodMonth, setPeriodMonth] = useState('')
  const [failed, setFailed] = useState<string | null>(null)

  const run = async () => {
    setFailed(null)
    try {
      const result = await start.mutateAsync({ period_month: periodMonth || undefined })
      onStarted(result.payout_id)
    } catch (error) {
      setFailed(
        isInvalidPeriodMonth(error)
          ? t('batchPayout.start.invalidMonth')
          : t('batchPayout.start.failed'),
      )
    }
  }

  return (
    <div className="card card-pad bpo-start" data-testid="bpo-start-panel">
      <h2>{t('batchPayout.start.title')}</h2>
      <p className="muted">{t('batchPayout.start.hint')}</p>

      <label className="bpo-start-month">
        <span>{t('batchPayout.start.monthLabel')}</span>
        <input
          type="month"
          className="input"
          value={periodMonth}
          onChange={(e) => setPeriodMonth(e.target.value)}
          aria-label={t('batchPayout.start.monthAria')}
          data-testid="bpo-start-month"
        />
      </label>

      {failed && (
        <p className="stl-sheet-error" role="alert" data-testid="bpo-start-error">
          {failed}
        </p>
      )}

      <button
        type="button"
        className="btn btn-primary"
        onClick={() => void run()}
        disabled={start.isPending}
        data-testid="bpo-start-cta"
      >
        {start.isPending ? t('batchPayout.start.pending') : t('batchPayout.start.cta')}
      </button>
    </div>
  )
}

// --- Uebersprungene Hosts (Punkt 4) -------------------------------------------

function SkippedSection({ skipped }: { skipped: BatchPayoutSkipped[] }) {
  const { t } = useTranslation('settlements')
  if (skipped.length === 0) return null

  return (
    <div className="card card-pad bpo-skipped" data-testid="bpo-skipped">
      <h3>{t('batchPayout.skipped.title', { count: skipped.length })}</h3>
      <p className="muted">{t('batchPayout.skipped.hint')}</p>
      <ul className="bpo-skipped-list">
        {skipped.map((s) => (
          <li key={s.host_id} className="bpo-skipped-row" data-testid={`bpo-skipped-${s.host_id}`}>
            <span className="bpo-skipped-name">{s.host_name ?? t('noValue')}</span>
            <span className="muted bpo-skipped-reason">
              {t(`batchPayout.skipped.reason.${s.reason}`)}
            </span>
            <Link
              to="/hosts/$id"
              params={{ id: String(s.host_id) }}
              className="btn btn-sm btn-secondary"
              data-testid={`bpo-skipped-edit-${s.host_id}`}
            >
              {t('batchPayout.skipped.editHost')}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

// --- Empfaengerliste + QR eines Blocks ----------------------------------------

function SingleQrCell({
  payee,
  payoutMap,
}: {
  payee: BatchPayoutPayee
  payoutMap: Map<number, string | null>
}) {
  const { t } = useTranslation('settlements')

  if (payee.settlement_ids.length !== 1) {
    return (
      <p className="muted bpo-single-qr-hint" data-testid="bpo-single-qr-unavailable-multi">
        {t('batchPayout.singleQrUnavailableMulti')}
      </p>
    )
  }
  const uri = payoutMap.get(payee.settlement_ids[0])
  if (!uri) {
    return (
      <p className="muted bpo-single-qr-hint" data-testid="bpo-single-qr-unavailable-unknown">
        {t('batchPayout.singleQrUnavailableUnknown')}
      </p>
    )
  }
  return (
    <div className="stl-qr-frame bpo-single-qr-frame" data-testid="bpo-single-qr">
      <QRCodeSVG value={uri} size={QR_SIZE_SINGLE} level="M" marginSize={2} />
    </div>
  )
}

function BlockView({
  block,
  singleQrMode,
  payoutMap,
  onMarkPaid,
  markPending,
  markFailed,
}: {
  block: BatchPayoutBlock
  singleQrMode: boolean
  payoutMap: Map<number, string | null>
  onMarkPaid: () => void
  markPending: boolean
  markFailed: boolean
}) {
  const { t } = useTranslation('settlements')

  return (
    <div className="bpo-block" data-testid={`bpo-block-${block.id}`}>
      {!singleQrMode && (
        <div className="card card-pad stl-qr-card bpo-qr-card">
          <div className="stl-qr-frame" data-testid="bpo-qr-multi">
            <QRCodeSVG value={block.qr_payload} size={QR_SIZE_MULTI} level="M" marginSize={2} />
          </div>
          <p className="muted stl-qr-hint">{t('batchPayout.qrHintMulti')}</p>
        </div>
      )}
      {singleQrMode && (
        <p className="muted stl-qr-hint bpo-single-qr-intro">{t('batchPayout.qrHintSingle')}</p>
      )}

      <div className="card card-pad bpo-payees">
        <div className="list-head bpo-payee-grid" data-testid="bpo-payee-head">
          <div>{t('batchPayout.table.host')}</div>
          <div className="stl-num">{t('batchPayout.table.amount')}</div>
          <div>{t('batchPayout.table.memo')}</div>
        </div>
        <div className="list bpo-payee-list">
          {block.payees.map((payee, i) => (
            <div
              key={`${payee.address}-${i}`}
              className="list-row bpo-payee-grid"
              data-testid={`bpo-payee-${block.id}-${i}`}
            >
              <span className="ellipsis">{payee.host_name ?? t('noValue')}</span>
              <span className="mono tnum stl-num">{fmtHnt(payee.amount)}</span>
              <span className="muted ellipsis">{payee.memo}</span>
              {singleQrMode && (
                <div className="bpo-payee-single-qr">
                  <SingleQrCell payee={payee} payoutMap={payoutMap} />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="bpo-block-total">
          <span>{t('batchPayout.blockTotal')}</span>
          <strong className="mono tnum">{fmtHnt(block.total_amount)}</strong>
        </div>
      </div>

      {markFailed && (
        <p className="stl-sheet-error" role="alert" data-testid="bpo-mark-paid-error">
          {t('batchPayout.markPaidFailed')}
        </p>
      )}

      <div className="stl-sheet-actions bpo-block-actions">
        {block.paid ? (
          <p className="muted" data-testid="bpo-already-paid">
            {t('batchPayout.alreadyPaid')}
          </p>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onMarkPaid}
            disabled={markPending}
            data-testid="bpo-mark-paid"
          >
            {markPending ? t('batchPayout.markPaidPending') : t('batchPayout.markPaid')}
          </button>
        )}
      </div>
    </div>
  )
}

// --- Abschluss-Zusammenfassung (Punkt 2, "am Ende Zusammenfassung") ----------

function SummaryPanel({ data, onStartNew }: { data: BatchPayoutResponse; onStartNew: () => void }) {
  const { t } = useTranslation('settlements')
  const recipientCount = data.blocks.reduce((sum, b) => sum + b.payees.length, 0)
  const totalAmount = data.blocks.reduce((sum, b) => sum + (b.total_amount ?? 0), 0)

  return (
    <div className="card card-pad bpo-summary" data-testid="bpo-summary">
      <div className="bpo-summary-icon" aria-hidden="true">
        <CheckCircle2 size={28} />
      </div>
      <h2>{t('batchPayout.summary.title')}</h2>
      <div className="stat-strip stl-sheet-stats">
        <div className="stat">
          <div className="stat-label">
            {t('batchPayout.summary.blocks', { count: data.blocks.length })}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">
            {t('batchPayout.summary.recipients', { count: recipientCount })}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">{t('batchPayout.summary.total')}</div>
          <div className="stat-value num-md">{fmtHnt(totalAmount)}</div>
        </div>
      </div>
      <div className="bpo-summary-actions">
        <Link to="/settlements" className="btn btn-secondary" data-testid="bpo-back-to-settlements">
          {t('batchPayout.backToSettlements')}
        </Link>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onStartNew}
          data-testid="bpo-start-new"
        >
          {t('batchPayout.summary.startNew')}
        </button>
      </div>
    </div>
  )
}

// --- Seite ---------------------------------------------------------------------

export function BatchPayoutPage() {
  const { t } = useTranslation('settlements')
  const navigate = useNavigate()
  const search = (useSearch({ strict: false }) ?? {}) as { payoutId?: number }
  const payoutId = search.payoutId

  const { account } = useAccount()
  const [singleQrMode, setSingleQrMode] = useState(false)
  const [initializedFor, setInitializedFor] = useState<number | null>(null)
  const [blockIndex, setBlockIndex] = useState(0)

  const detail = useBatchPayoutDetail(payoutId)
  const markPaid = useMarkBlockPaid(payoutId)
  // Fallback-Datenquelle fuer den Einzel-QR-Umschalter (siehe Kopfkommentar) —
  // keine Monatsfilterung: der Payout kann Abrechnungen mehrerer Monate
  // umfassen, wenn beim Start kein period_month gewaehlt wurde.
  const dueForFallback = useDueSettlements({})

  const data = detail.data

  if ((account && !account.is_premium) || isPremiumRequiredError(detail.error)) {
    return <PremiumRequired message={t('premiumRequired')} data-testid="batch-payout-premium-required" />
  }

  if (data && initializedFor !== data.payout_id) {
    setInitializedFor(data.payout_id)
    const firstUnpaid = data.blocks.findIndex((b) => !b.paid)
    setBlockIndex(firstUnpaid >= 0 ? firstUnpaid : Math.max(0, data.blocks.length - 1))
  }

  const goToPayout = (id: number) => {
    void navigate({ to: '/settlements/payout', search: { payoutId: id }, replace: true })
  }
  const startNew = () => {
    void navigate({ to: '/settlements/payout', search: {}, replace: true })
  }

  const payoutMap = new Map<number, string | null>()
  for (const s of dueForFallback.settlements) {
    if (s.id !== undefined) payoutMap.set(s.id, s.payout?.solana_pay_uri ?? null)
  }

  return (
    <section data-testid="batch-payout-page" className="bpo-page">
      <div className="bpo-header">
        <h1>{t('batchPayout.title')}</h1>
        <Link to="/settlements" className="btn btn-secondary" data-testid="bpo-header-back">
          {t('batchPayout.backToSettlements')}
        </Link>
      </div>

      {payoutId === undefined && <StartPanel onStarted={goToPayout} />}

      {payoutId !== undefined && detail.isLoading && (
        <div
          className="stl-list"
          aria-busy="true"
          aria-label={t('batchPayout.loading')}
          data-testid="bpo-loading"
        >
          <div className="stl-skeleton">
            {Array.from({ length: 3 }).map((_, i) => (
              <div className="skeleton stl-skeleton-row" aria-hidden="true" key={i} />
            ))}
          </div>
        </div>
      )}

      {payoutId !== undefined && detail.isError && (
        <div className="card stl-state" role="alert" data-testid="bpo-error">
          <p>{t('batchPayout.error.load')}</p>
          <div className="bpo-error-actions">
            <button type="button" className="btn btn-primary" onClick={() => void detail.refetch()}>
              {t('batchPayout.error.retry')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={startNew}
              data-testid="bpo-error-start-new"
            >
              {t('batchPayout.error.startNew')}
            </button>
          </div>
        </div>
      )}

      {payoutId !== undefined && data && data.blocks.length === 0 && (
        <div className="card stl-state" data-testid="bpo-empty">
          <p className="stl-state-title">{t('batchPayout.empty.title')}</p>
          <p className="muted stl-state-hint">{t('batchPayout.empty.hint')}</p>
        </div>
      )}

      {payoutId !== undefined && data && data.blocks.length === 0 && (
        <SkippedSection skipped={data.skipped} />
      )}

      {payoutId !== undefined && data && data.blocks.length > 0 && (
        <>
          {data.blocks.every((b) => b.paid) ? (
            <SummaryPanel data={data} onStartNew={startNew} />
          ) : (
            <>
              <div
                className="bpo-block-nav"
                role="tablist"
                aria-label={t('batchPayout.blockNav', { current: 1, total: data.blocks.length })}
              >
                {data.blocks.map((b, i) => {
                  const active = i === Math.min(blockIndex, data.blocks.length - 1)
                  return (
                    <button
                      key={b.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`bpo-block-dot${active ? ' is-active' : ''}${b.paid ? ' is-paid' : ''}`}
                      onClick={() => setBlockIndex(i)}
                      data-testid={`bpo-block-dot-${b.id}`}
                    >
                      {b.paid ? <CheckCircle2 size={14} aria-hidden="true" /> : i + 1}
                    </button>
                  )
                })}
              </div>

              <p className="muted" data-testid="bpo-block-progress">
                {t('batchPayout.blockNav', {
                  current: Math.min(blockIndex, data.blocks.length - 1) + 1,
                  total: data.blocks.length,
                })}
              </p>

              <label className="bpo-single-qr-toggle">
                <input
                  type="checkbox"
                  checked={singleQrMode}
                  onChange={(e) => setSingleQrMode(e.target.checked)}
                  data-testid="bpo-single-qr-toggle"
                />
                {t('batchPayout.singleQrToggle')}
              </label>

              <BlockView
                block={data.blocks[Math.min(blockIndex, data.blocks.length - 1)]}
                singleQrMode={singleQrMode}
                payoutMap={payoutMap}
                markPending={markPaid.isPending}
                markFailed={markPaid.isError}
                onMarkPaid={() =>
                  markPaid.mutate(data.blocks[Math.min(blockIndex, data.blocks.length - 1)].id)
                }
              />

              <div className="bpo-sequence-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setBlockIndex((idx) => Math.max(0, idx - 1))}
                  disabled={blockIndex <= 0}
                  data-testid="bpo-prev"
                >
                  <ArrowLeft size={16} aria-hidden="true" /> {t('batchPayout.prev')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setBlockIndex((idx) => Math.min(data.blocks.length - 1, idx + 1))}
                  disabled={blockIndex >= data.blocks.length - 1}
                  data-testid="bpo-next"
                >
                  {t('batchPayout.next')} <ArrowRight size={16} aria-hidden="true" />
                </button>
              </div>

              <SkippedSection skipped={data.skipped} />
            </>
          )}
        </>
      )}
    </section>
  )
}
